import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PostgrestError } from "@supabase/supabase-js";

export interface OrderOverview {
  id: string;
  order_code: string | null;
  customer_name: string | null;
  business_unit: string | null;
  order_type: string | null;
  status: string | null;
  fishsix_student_code: string | null;
  esc_student_code: string | null;
  knoware_student_code: string | null;
  lead_number: string | null;
  products: Array<{
    product_name?: string;
    variant_name?: string;
    variant_price?: number;
    quantity?: number;
    [key: string]: any;
  }> | null;
  salespersons: Array<{
    name?: string;
    percentage?: number;
    amount?: number;
    [key: string]: any;
  }> | null;
  promotions: Array<any> | null;
  manual_add_hours_value: number | null;
  manual_add_hours_at: string | null;
  manual_add_hour_by: string | null;
}

interface UseOrderOverviewParams {
  page: number;
  pageSize: number;
  searchTerm: string;
  orderType: string;
  status: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
}

export const useOrderOverview = ({
  page,
  pageSize,
  searchTerm,
  orderType,
  status,
  sortBy,
  sortOrder,
}: UseOrderOverviewParams) => {
  return useQuery({
    queryKey: [
      "order-overview",
      page,
      pageSize,
      searchTerm,
      orderType,
      status,
      sortBy,
      sortOrder,
    ],
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Build the query with Fishsix filter - use any type for view
      const baseQuery = supabase
        .from("order_overview" as any)
        .select("*", { count: "exact" })
        .eq("business_unit", "Fishsix");

      let query = baseQuery;

      // Apply search filter
      if (searchTerm) {
        query = query.or(
          `order_code.ilike.%${searchTerm}%,customer_name.ilike.%${searchTerm}%,fishsix_student_code.ilike.%${searchTerm}%`
        );
      }

      // Apply order type filter
      if (orderType && orderType !== "all") {
        query = query.eq("order_type", orderType);
      }

      // Apply status filter
      if (status && status !== "all") {
        query = query.eq("status", status);
      }

      // Apply sorting
      if (sortBy) {
        query = query.order(sortBy, { ascending: sortOrder === "asc" });
      } else {
        query = query.order("order_code", { ascending: false });
      }

      // Apply pagination
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: ((data || []) as unknown) as OrderOverview[],
        count: count || 0,
      };
    },
  });
};
